import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar, Toast, Tag, Skeleton, Progress } from 'react-vant';
import { getSessions } from '../../api/club';
import request from '../../api/request';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { ClockO, FireO, FriendsO } from '@react-vant/icons';

const ClubList = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector(state => state.user);
  const basePath = userInfo?.role === 'parent' || userInfo?.role === 'student' ? '/student/clubs' : '/clubs';
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActiveSessions();
  }, []);

  const fetchActiveSessions = async () => {
    try {
      setLoading(true);
      
      // 获取当前学年进行过滤
      const params = { page: 1, page_size: 50 };
      try {
        const yearRes = await request.get('/academic-year/current/');
        const currentYear = yearRes.data?.current_academic_year || yearRes.current_academic_year;
        if (currentYear) params.academic_year = currentYear;
      } catch (e) {
        console.error('获取学年失败:', e);
      }
      
      const res = await getSessions(params);
      setSessions(res.items || []);
    } catch (error) {
      Toast.fail('获取社团列表失败');
    } finally {
      setLoading(false);
    }
  };

  const renderStatusTag = (status, currentCount, maxCapacity) => {
    if (status === 'upcoming') {
      return <div style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ddd6fe 100%)', color: '#fff', padding: '4px 12px', borderRadius: '12px 0 16px 0', fontSize: 12, fontWeight: 700, position: 'absolute', top: 0, left: 0 }}>未开始</div>;
    }
    if (status === 'ongoing') {
      return <div style={{ background: 'linear-gradient(135deg, #ff5e1e 0%, #ea580c 100%)', color: '#fff', padding: '4px 12px', borderRadius: '12px 0 16px 0', fontSize: 12, fontWeight: 700, position: 'absolute', top: 0, left: 0 }}>活动中</div>;
    }
    if (status === 'closed') {
      return <div style={{ background: '#e2e2e2', color: '#94a3b8', padding: '4px 12px', borderRadius: '12px 0 16px 0', fontSize: 12, fontWeight: 700, position: 'absolute', top: 0, left: 0 }}>已结束</div>;
    }
    if (currentCount >= maxCapacity) {
      return <div style={{ background: 'linear-gradient(135deg, #fa8c16 0%, #fbbf24 100%)', color: '#fff', padding: '4px 12px', borderRadius: '12px 0 16px 0', fontSize: 12, fontWeight: 700, position: 'absolute', top: 0, left: 0 }}>名额满·可候补</div>;
    }
    return <div style={{ background: 'linear-gradient(135deg, #00b578 0%, #5eead4 100%)', color: '#fff', padding: '4px 12px', borderRadius: '12px 0 16px 0', fontSize: 12, fontWeight: 700, position: 'absolute', top: 0, left: 0 }}>报名中</div>;
  };

  return (
    <div className="club-list-page" style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: '"Inter", "PingFang SC", "Microsoft YaHei", sans-serif' }}>
      <div style={{ 
          background: 'rgba(255, 255, 255, 0.85)', 
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          position: 'sticky', 
          top: 0, 
          zIndex: 100,
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
      }}>
          <NavBar 
            title={<span style={{ fontWeight: 800, fontSize: 18, color: '#0f172a' }}>社团报名</span>} 
            onClickLeft={() => navigate(-1)} 
            rightText={<span style={{ fontWeight: 600, color: '#056b4f' }}>我的报名</span>}
            onClickRight={() => navigate(`${basePath}/my-registrations`)}
            border={false}
          />
      </div>
      
      {loading ? (
        <div style={{ padding: '20px' }}>
          <div style={{ background: '#fff', padding: 20, borderRadius: 16, marginBottom: 16 }}><Skeleton title row={3} /></div>
          <div style={{ background: '#fff', padding: 20, borderRadius: 16 }}><Skeleton title row={3} /></div>
        </div>
      ) : (
        <div style={{ padding: '16px' }}>
          {sessions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', background: '#fff', borderRadius: 20, boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🍃</div>
              <div style={{ fontSize: 16, fontWeight: 500 }}>当前没有开放报名的社团</div>
            </div>
          ) : (
            sessions.map((session) => {
              const percentage = Math.min(100, Math.round((session.current_count / session.max_capacity) * 100));
              const isFull = session.current_count >= session.max_capacity;
              
              return (
                <div 
                  key={session.id} 
                  style={{ 
                    background: '#fff', 
                    borderRadius: '16px', 
                    padding: '24px 20px 20px',
                    marginBottom: '16px',
                    boxShadow: '0 8px 24px rgba(149, 157, 165, 0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.8)'
                  }}
                  onClick={() => navigate(`${basePath}/register/${session.id}`)}
                >
                  {renderStatusTag(session.status, session.current_count, session.max_capacity)}
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', marginTop: '8px' }}>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: '#334155', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {session.club_name}
                      {(session.status === 'open' || session.status === 'active') && !isFull && <FireO color="#e11d48" size={20} />}
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.8' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, background: '#f8fafc', padding: '6px 12px', borderRadius: 8 }}>
                      <ClockO color="#056b4f" size={16} />
                      <span style={{ fontSize: 13 }}>
                        {dayjs(session.registration_start).format('MM-DD HH:mm')} 至 {dayjs(session.registration_end).format('MM-DD HH:mm')}
                      </span>
                    </div>

                    <div style={{ marginTop: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: 13, fontWeight: 600 }}>
                        <span style={{ color: '#334155' }}>报名进度</span>
                        <span style={{ color: isFull ? '#e11d48' : '#00b578' }}>{session.current_count} / {session.max_capacity}</span>
                      </div>
                      <Progress 
                        percentage={percentage} 
                        pivotText="" 
                        color={isFull ? "linear-gradient(to right, #ff5e1e, #e11d48)" : "linear-gradient(to right, #00b578, #34d399)"} 
                        trackColor="#f1f5f9"
                        strokeWidth={8}
                      />
                    </div>

                    {session.waitlist_count > 0 && (
                      <div style={{ marginTop: 12, alignItems: 'center', gap: 6, fontSize: 12, color: '#d97706', background: '#fff5f0', padding: '4px 10px', borderRadius: 12, display: 'inline-flex' }}>
                        <FriendsO size={14} /> 有 {session.waitlist_count} 人正在排队候补
                      </div>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ClubList;
